import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { useNodeId, useNodesData } from '@xyflow/react'
import * as Y from 'yjs'
import diff from 'fast-diff'
import { createNodeData } from '@/shared/lib/node-registry'
import { useNodeCustomMap } from './useNodeCustomMap'

function ensureYTextField(
  yNodeDataMap: Y.Map<unknown>,
  field: string,
  initialValue: string,
): Y.Text {
  const existing = yNodeDataMap.get(field)

  if (existing instanceof Y.Text) {
    return existing
  }

  const text = new Y.Text()
  const source = typeof existing === 'string' ? existing : initialValue

  if (source.length > 0) {
    text.insert(0, source)
  }

  yNodeDataMap.set(field, text)
  return text
}

function toYMap(value: Record<string, unknown>, seen = new Set<unknown>()): Y.Map<unknown> {
  const map = new Y.Map<unknown>()

  if (seen.has(value)) {
    return map
  }
  seen.add(value)

  Object.entries(value).forEach(([nestedKey, nestedValue]) => {
    // 不要深入遍历已经被 Yjs 包装过的类型，防止内部循环引用导致爆栈
    if (nestedValue instanceof Y.AbstractType) {
      return
    }

    if (nestedValue && typeof nestedValue === 'object' && !Array.isArray(nestedValue)) {
      map.set(nestedKey, toYMap(nestedValue as Record<string, unknown>, seen))
      return
    }

    map.set(nestedKey, nestedValue)
  })

  return map
}

function ensureYMap(parent: Y.Map<unknown>, key: string): Y.Map<unknown> {
  const existing = parent.get(key)

  if (existing instanceof Y.Map) {
    return existing
  }

  // 如果 existing 是其他的 Yjs 类型（比如 Y.Text），不能把它当普通对象遍历
  if (existing instanceof Y.AbstractType) {
    const map = new Y.Map<unknown>()
    parent.set(key, map)
    return map
  }

  const map = existing && typeof existing === 'object' && !Array.isArray(existing)
    ? toYMap(existing as Record<string, unknown>)
    : new Y.Map<unknown>()

  parent.set(key, map)
  return map
}

function readCurrentOp(yNodeDataMap: Y.Map<unknown>) {
  const op = yNodeDataMap.get('op')
  return typeof op === 'string' ? op : ''
}

function applyDiffToYText(yText: Y.Text, nextValue: string) {
  const currentValue = yText.toString()
  if (currentValue === nextValue) {
    return
  }

  const diffs = diff(currentValue, nextValue)
  let cursor = 0

  const applyChange = () => {
    diffs.forEach(([operation, text]) => {
      if (operation === diff.EQUAL) {
        cursor += text.length
        return
      }

      if (operation === diff.DELETE) {
        yText.delete(cursor, text.length)
        return
      }

      if (operation === diff.INSERT) {
        yText.insert(cursor, text)
        cursor += text.length
      }
    })
  }

  if (yText.doc) {
    yText.doc.transact(applyChange)
    return
  }

  applyChange()
}

function getDefaultTextValue(nodeType: string, field: string) {
  const defaultData = createNodeData(nodeType) as unknown as Record<string, unknown>
  const defaultValue = defaultData[field]

  return typeof defaultValue === 'string' ? defaultValue : ''
}

function useSyncedYTextValue(
  yTextMap: Y.Map<unknown> | null,
  field: string,
  initialValue: string,
) {
  const [yText, setYText] = useState<Y.Text | null>(null)
  const [value, setValue] = useState(initialValue)
  const yTextRef = useRef<Y.Text | null>(null)
  const yTextMapRef = useRef<Y.Map<unknown> | null>(null)
  const pendingValueRef = useRef<string | null>(null)
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  yTextMapRef.current = yTextMap

  const clearSyncTimer = useCallback(() => {
    if (!syncTimerRef.current) {
      return
    }

    clearTimeout(syncTimerRef.current)
    syncTimerRef.current = null
  }, [])

  const writeValue = useCallback((nextValue: string) => {
    const currentMap = yTextMapRef.current
    const currentYText = currentMap
      ? ensureYTextField(currentMap, field, initialValue)
      : yTextRef.current

    if (!currentYText) {
      return false
    }

    yTextRef.current = currentYText
    applyDiffToYText(currentYText, nextValue)
    return true
  }, [field, initialValue])

  const flushPendingValue = useCallback(() => {
    const pendingValue = pendingValueRef.current
    if (pendingValue === null) {
      return
    }

    if (writeValue(pendingValue)) {
      pendingValueRef.current = null
    }
  }, [writeValue])

  useEffect(() => {
    if (!yTextMap) {
      yTextRef.current = null
      setYText(null)
      setValue(initialValue)
      return
    }

    let observedYText: Y.Text | null = null

    const textObserver = () => {
      if (!observedYText) {
        return
      }

      setValue(observedYText.toString())
    }

    const bindYText = (nextYText: Y.Text) => {
      if (observedYText === nextYText) {
        setValue(nextYText.toString())
        return
      }

      if (observedYText) {
        observedYText.unobserve(textObserver)
      }

      observedYText = nextYText
      yTextRef.current = nextYText
      setYText(nextYText)
      setValue(nextYText.toString())
      nextYText.observe(textObserver)
    }

    const syncYTextField = () => {
      bindYText(ensureYTextField(yTextMap, field, initialValue))
    }

    const mapObserver = (event: Y.YMapEvent<unknown>) => {
      if (event.keysChanged.has(field)) {
        syncYTextField()
      }
    }

    syncYTextField()
    yTextMap.observe(mapObserver)

    return () => {
      yTextMap.unobserve(mapObserver)
      if (observedYText) {
        observedYText.unobserve(textObserver)
      }
    }
  }, [field, initialValue, yTextMap])

  useEffect(
    () => () => {
      clearSyncTimer()
      flushPendingValue()
    },
    [clearSyncTimer, flushPendingValue],
  )

  const updateValue = useCallback(
    (nextValue: string) => {
      setValue(nextValue)
      pendingValueRef.current = nextValue
      clearSyncTimer()

      syncTimerRef.current = setTimeout(() => {
        syncTimerRef.current = null
        flushPendingValue()
      }, 300)
    },
    [clearSyncTimer, flushPendingValue],
  )

  return {
    ready: yText !== null,
    value,
    setValue: updateValue,
  }
}

export function useYText(field: string, initialValue?: string, explicitNodeId?: string) {
  const contextNodeId = useNodeId()
  const nodeId = explicitNodeId ?? contextNodeId

  if (!nodeId) {
    throw new Error('useYText must be used inside a React Flow node.')
  }

  const flowNodeData = useNodesData(nodeId)
  const nodeType = flowNodeData?.type ?? 'default'
  const resolvedInitialValue = initialValue ?? getDefaultTextValue(nodeType, field)
  const yNodeDataMap = useNodeCustomMap(nodeId)

  return useSyncedYTextValue(yNodeDataMap, field, resolvedInitialValue)
}

export function useNodeOpYText(field: string, initialValue?: string, explicitNodeId?: string) {
  const contextNodeId = useNodeId()
  const nodeId = explicitNodeId ?? contextNodeId

  if (!nodeId) {
    throw new Error('useNodeOpYText must be used inside a React Flow node.')
  }

  const resolvedInitialValue = initialValue ?? ''
  const yNodeDataMap = useNodeCustomMap(nodeId)
  const [currentOpData, setCurrentOpData] = useState<Y.Map<unknown> | null>(null)
  const subscribeCurrentOp = useCallback(
    (onStoreChange: () => void) => {
      if (!yNodeDataMap) {
        return () => { }
      }

      const observer = (event: Y.YMapEvent<unknown>) => {
        if (event.keysChanged.has('op')) {
          onStoreChange()
        }
      }

      yNodeDataMap.observe(observer)
      return () => yNodeDataMap.unobserve(observer)
    },
    [yNodeDataMap],
  )
  const getCurrentOpSnapshot = useCallback(
    () => yNodeDataMap ? readCurrentOp(yNodeDataMap) : '',
    [yNodeDataMap],
  )
  const currentOp = useSyncExternalStore(
    subscribeCurrentOp,
    getCurrentOpSnapshot,
    () => '',
  )

  useEffect(() => {
    if (!yNodeDataMap || !currentOp) {
      setCurrentOpData(null)
      return
    }

    const opDataRoot = ensureYMap(yNodeDataMap, 'op_data')
    setCurrentOpData(ensureYMap(opDataRoot, currentOp))
  }, [currentOp, yNodeDataMap])

  return useSyncedYTextValue(currentOpData, field, resolvedInitialValue)
}
